import toast from './toast';

/**
 * Type guard to check if an error object contains a data.error message
 * (the shape RTK Query mutations reject with).
 * @param error The error object to check
 * @returns Type predicate indicating if the error has a data.error message
 */
export const isErrorWithMessage = (error: unknown): error is { data: { error: string } } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'error' in error.data &&
    typeof error.data.error === 'string'
  );
};

interface AxiosLikeErrorResponseData {
  detail?: string;
  message?: string;
  error?: string;
  non_field_errors?: string[];
}

interface AxiosLikeError {
  response?: { data?: AxiosLikeErrorResponseData | string };
  message?: string;
}

/**
 * Type guard for axios-style errors, i.e. `{ response: { data: ... }, message }`.
 */
const isAxiosLikeError = (error: unknown): error is AxiosLikeError => {
  return typeof error === 'object' && error !== null && 'response' in error;
};

/**
 * Extracts a human-readable error message from any of the error shapes used
 * across this app: RTK Query rejections (`{ data: { error } }`), axios errors
 * (`error.response.data.{detail,message,error,non_field_errors}`), native
 * `Error` instances, and plain strings.
 * @param error The error object
 * @param fallback Message to use when no known shape matches (default: 'An unknown error occurred')
 * @returns A string error message
 */
export const getErrorMessage = (error: unknown, fallback: string = 'An unknown error occurred'): string => {
  if (isErrorWithMessage(error)) {
    return error.data.error;
  }

  if (isAxiosLikeError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data) {
      return data;
    }
    if (data && typeof data === 'object') {
      if (data.detail) return data.detail;
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
        return data.non_field_errors[0];
      }
    }
    if (error.message) return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string' && error) {
    return error;
  }

  return fallback;
};

/**
 * Extracts a human-readable message from a caught error and shows it as an
 * error toast in one call, e.g. `catch (err) { showErrorToast(err); }`.
 * @param error The error object
 * @param fallback Message to use when no known shape matches
 */
export const showErrorToast = (error: unknown, fallback?: string): void => {
  toast.error(getErrorMessage(error, fallback));
};